/**
 * JOAltTextInjector — Rewrites product image `alt` attributes with SEO-optimized
 * alt text (stored in product.alt_text after Claude Sonnet enrichment).
 *
 * Works on:
 *   - /shop, /boutique (product cards)
 *   - /product/:slug, /produit/:slug (main product images)
 *
 * NON-CORE — additive.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isShopRoute(pathname) {
  return (
    pathname === "/shop" ||
    pathname.startsWith("/shop/") ||
    pathname === "/boutique" ||
    pathname.startsWith("/boutique/")
  );
}

function applyAlt(products) {
  const altBySlug = new Map();
  for (const p of products) {
    if (p.alt_text && p.slug) altBySlug.set(p.slug, p.alt_text);
  }
  if (altBySlug.size === 0) return;

  // Shop cards
  document.querySelectorAll('[data-testid^="shop-product-"]').forEach((card) => {
    const slug = (card.getAttribute("data-testid") || "").replace(/^shop-product-/, "");
    const alt = altBySlug.get(slug);
    if (!alt) return;
    card.querySelectorAll("img").forEach((img) => {
      if (img.alt !== alt) img.alt = alt;
    });
  });

  // Product detail page: URL is /shop/<slug>. Match by URL slug.
  const detail = window.location.pathname.match(/^\/shop\/([^/?#]+)$/);
  if (detail) {
    const alt = altBySlug.get(detail[1]);
    if (alt) {
      document.querySelectorAll('[data-testid="product-media"] img, main img').forEach((img) => {
        const src = img.getAttribute("src") || "";
        if (src.includes("/uploads/products/")) {
          if (img.alt !== alt) img.alt = alt;
        }
      });
    }
  }
}

export default function JOAltTextInjector() {
  const location = useLocation();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!isShopRoute(location.pathname)) return;
    let alive = true;
    axios.get(`${API}/shop/products`).then((r) => {
      if (alive) setProducts(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [location.pathname]);

  useEffect(() => {
    if (!isShopRoute(location.pathname) || products.length === 0) return;
    const run = () => applyAlt(products);
    run();
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      run();
      if (tries > 15) clearInterval(interval);
    }, 350);
    const observer = new MutationObserver(run);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [products, location.pathname]);

  return null;
}
